import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';

function Framework() {
  const [isLoading, setIsLoading] = useState(true);
  const [frameworks, setFrameworks] = useState([]);
  const [userFramework, setUserFramework] = useState(null);
  const [mcpServer, setMcpServer] = useState(null);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState('');
  const [mcpConfig, setMcpConfig] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [configureResult, setConfigureResult] = useState(null);
  
  useEffect(() => {
    const fetchFrameworkData = async () => {
      try {
        setIsLoading(true);
        setError('');
        
        console.log('Fetching framework data from API...');
        const response = await axios.get(`${API_URL}/frameworks/user`);
        
        console.log('Framework API response:', response.data);
        
        // Check if response has the expected structure
        if (response.data.frameworks) {
          setFrameworks(response.data.frameworks || []);
          setUserFramework(response.data.userFramework || null);
          setMcpServer(response.data.mcpServer || null);
          
          // Set existing config values if user has a framework configured
          if (response.data.userFramework) {
            setSelectedFrameworkId(response.data.userFramework.framework_id);
            
            // Parse and set existing MCP config
            if (response.data.userFramework.mcp_config) {
              const existingConfig = typeof response.data.userFramework.mcp_config === 'string' 
                ? JSON.parse(response.data.userFramework.mcp_config)
                : response.data.userFramework.mcp_config;
              setMcpConfig(existingConfig);
            }
          }
        } else {
          throw new Error('Invalid response format from server');
        }
        
      } catch (err) {
        console.error('Error fetching framework data:', err);
        
        if (err.response?.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to load framework data. Please check your connection.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFrameworkData();
  }, []);
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFrameworkId) {
      setError('Please select a framework');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setIsSubmitting(true);
      
      const configData = { ...mcpConfig };
      
      // Set a store ID if none exists
      if (!configData.store_id) {
        // Generate unique store ID using user-specific information
        const timestamp = new Date().getTime();
        configData.store_id = `user_${timestamp}_store`;
      }
      
      const response = await axios.post(`${API_URL}/frameworks/user`, {
        frameworkId: selectedFrameworkId,
        mcpConfig: configData
      });
      
      setSuccess('Framework settings saved successfully');
      
      // Refresh data after successful save
      setTimeout(async () => {
        const refreshResponse = await axios.get(`${API_URL}/frameworks/user`);
        setUserFramework(refreshResponse.data.userFramework);
        setMcpServer(refreshResponse.data.mcpServer);
      }, 1000);
    } catch (err) {
      console.error('Error saving framework:', err);
      setError(err.response?.data?.message || 'Error saving framework settings');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleFrameworkChange = (e) => {
    const newFrameworkId = e.target.value;
    setSelectedFrameworkId(newFrameworkId);
    
    // Reset MCP config when framework changes
    if (userFramework && userFramework.framework_id === parseInt(newFrameworkId)) {
      // If switching back to previously configured framework, restore saved config
      try {
        const config = typeof userFramework.mcp_config === 'string' 
          ? JSON.parse(userFramework.mcp_config) 
          : userFramework.mcp_config;
        
        setMcpConfig(config || {});
      } catch (e) {
        console.error('Error parsing saved MCP config:', e);
        setMcpConfig({});
      }
    } else {
      // New framework selected, initialize with store_id only
      setMcpConfig({ store_id: mcpConfig.store_id || '' });
    }
  };
  
  const handleConfigChange = (field, value) => {
    setMcpConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Get the selected framework's MCP server
  const getSelectedFrameworkMcp = () => {
    if (!selectedFrameworkId || !frameworks.length) return null;
    
    const selectedFramework = frameworks.find(f => f.id === parseInt(selectedFrameworkId));
    if (!selectedFramework) return null;
    
    return mcpServer;
  };
  
  const selectedMcp = getSelectedFrameworkMcp();
  
  // Add this helper to check if selected framework is WooCommerce
  const isWooCommerce = () => {
    const fw = frameworks.find(f => f.id === parseInt(selectedFrameworkId));
    return fw && fw.name && fw.name.toLowerCase().includes('woocommerce');
  };

  // Handler for Configure button
  const handleConfigureWooCommerce = async () => {
    setIsConfiguring(true);
    setConfigureResult(null);
    try {
      // Ensure mcpServer is available and has correct structure
      if (!mcpServer || !mcpServer.woocommerce_admin || !mcpServer.woocommerce_customer) {
        setConfigureResult({ error: 'MCP server information is missing or incomplete. Please contact support.' });
        setIsConfiguring(false);
        return;
      }

      // Admin MCP
      const adminMcp = mcpServer.woocommerce_admin;
      const adminUrl = adminMcp.base_url?.replace(/\/$/, '') + '/store/configure';
      const adminApiKey = adminMcp.api_key;
      const adminPayload = {
        storeId: mcpConfig.store_id || mcpConfig.storeId,
        url: mcpConfig.url,
        consumerKey: mcpConfig.consumer_key || mcpConfig.consumerKey,
        consumerSecret: mcpConfig.consumer_secret || mcpConfig.consumerSecret
      };
      const adminHeaders = {
        'Content-Type': 'application/json',
      };
      if (adminApiKey) {
        adminHeaders['Authorization'] = `Bearer ${adminApiKey}`;
      }
      // Add console logs for debugging
      console.log('[WooCommerce Admin MCP] Configure URL:', adminUrl);
      console.log('[WooCommerce Admin MCP] Payload:', adminPayload);
      console.log('[WooCommerce Admin MCP] Headers:', adminHeaders);
      const adminRes = await fetch(adminUrl, {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify(adminPayload)
      });
      const adminJson = await adminRes.json();

      // Customer MCP
      const customerMcp = mcpServer.woocommerce_customer;
      const customerUrl = customerMcp.base_url?.replace(/\/$/, '') + '/configure';
      const customerApiKey = customerMcp.api_key;
      const customerPayload = {
        site_key: mcpConfig.store_id || mcpConfig.site_key,
        base_url: mcpConfig.url || mcpConfig.base_url
      };
      const customerHeaders = {
        'Content-Type': 'application/json',
      };
      if (customerApiKey) {
        customerHeaders['Authorization'] = `Bearer ${customerApiKey}`;
      }
      // Add console logs for debugging
      console.log('[WooCommerce Customer MCP] Configure URL:', customerUrl);
      console.log('[WooCommerce Customer MCP] Payload:', customerPayload);
      console.log('[WooCommerce Customer MCP] Headers:', customerHeaders);
      const customerRes = await fetch(customerUrl, {
        method: 'POST',
        headers: customerHeaders,
        body: JSON.stringify(customerPayload)
      });
      const customerJson = await customerRes.json();

      setConfigureResult({
        admin: adminJson,
        customer: customerJson
      });
    } catch (err) {
      setConfigureResult({ error: err.message });
    } finally {
      setIsConfiguring(false);
    }
  };

  // Helper to check if selected framework is Shopify
  const isShopify = () => {
    const fw = frameworks.find(f => f.id === parseInt(selectedFrameworkId));
    return fw && fw.name && fw.name.toLowerCase().includes('shopify');
  };

  // Handler for Configure Shopify MCP
  const handleConfigureShopify = async () => {
    setIsConfiguring(true);
    setConfigureResult(null);
    try {
      // Hardcoded Shopify MCP configure URL
      const configureUrl = 'https://square.techspawn.net/yasha/wp-json/shopify-mcp/v1/configure';
      // Use store_id and url as userId and shopDomain
      const payload = {
        userId: mcpConfig.store_id,
        shopDomain: mcpConfig.url
      };
      const headers = { 'Content-Type': 'application/json' };
      // Shopify MCP does not require API key for /configure
      const res = await fetch(configureUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      setConfigureResult({ shopify: json });
    } catch (err) {
      setConfigureResult({ error: err.message });
    } finally {
      setIsConfiguring(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading framework data...</p>
      </div>
    );
  }
  
  return (
    <Card className="shadow-sm">
      <Card.Header>
        <h5 className="m-0">Framework Configuration</h5>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {configureResult && (
          <Alert variant={configureResult.error ? 'danger' : 'info'} className="mt-3">
            {configureResult.error ? (
              <>Configure error: {configureResult.error}</>
            ) : (
              <>
                {configureResult.admin && <div><b>Admin MCP:</b> {JSON.stringify(configureResult.admin)}</div>}
                {configureResult.customer && <div className="mt-2"><b>Customer MCP:</b> {JSON.stringify(configureResult.customer)}</div>}
                {configureResult.shopify && <div className="mt-2"><b>Shopify MCP:</b> {JSON.stringify(configureResult.shopify)}</div>}
              </>
            )}
          </Alert>
        )}
        
        {frameworks.length === 0 ? (
          <Alert variant="warning">
            No active frameworks available. Please contact the administrator.
          </Alert>
        ) : (
          <>
            <div className="mb-4">
              <p>
                Select the e-commerce framework for your business. This determines which platform your AI chatbot will connect to.
              </p>
              
              {userFramework && (
                <Alert variant="info">
                  <i className="fas fa-info-circle me-2"></i>
                  You are currently using the <strong>{userFramework.framework_name}</strong> framework.
                </Alert>
              )}
            </div>
            
            <Form onSubmit={handleFormSubmit}>
              <Form.Group className="mb-4">
                <Form.Label>Select Framework</Form.Label>
                <Form.Select 
                  value={selectedFrameworkId} 
                  onChange={handleFrameworkChange}
                  required
                >
                  <option value="">-- Select Framework --</option>
                  {frameworks.map(framework => (
                    <option key={framework.id} value={framework.id}>
                      {framework.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              {selectedFrameworkId && selectedMcp && (
                <div className="mt-4">
                  <h5>Configuration for {frameworks.find(f => f.id === parseInt(selectedFrameworkId))?.name}</h5>
                  {/* WooCommerce: Hardcoded fields */}
                  {isWooCommerce() ? (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Store ID <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={mcpConfig.store_id || ''}
                          onChange={e => handleConfigChange('store_id', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          A unique identifier for your store. This must be unique per store connection.
                        </Form.Text>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Store URL <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={mcpConfig.url || ''}
                          onChange={e => handleConfigChange('url', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          The base URL of your WooCommerce store (e.g., https://yourstore.com)
                        </Form.Text>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Consumer Key <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={mcpConfig.consumer_key || ''}
                          onChange={e => handleConfigChange('consumer_key', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          WooCommerce REST API Consumer Key
                        </Form.Text>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Consumer Secret <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="password"
                          value={mcpConfig.consumer_secret || ''}
                          onChange={e => handleConfigChange('consumer_secret', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          WooCommerce REST API Consumer Secret
                        </Form.Text>
                      </Form.Group>
                    </>
                  ) : isShopify() ? (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Store ID <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={mcpConfig.store_id || ''}
                          onChange={e => handleConfigChange('store_id', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          A unique identifier for your Shopify store. This must be unique per store connection.
                        </Form.Text>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Store URL <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={mcpConfig.url || ''}
                          onChange={e => handleConfigChange('url', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          Your Shopify store domain (e.g., mystore.myshopify.com)
                        </Form.Text>
                      </Form.Group>
                    </>
                  ) : (
                    // Other frameworks: dynamic fields from config_schema
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Store ID <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={mcpConfig.store_id || ''}
                          onChange={e => handleConfigChange('store_id', e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          A unique identifier for your store. This must be unique per store connection.
                        </Form.Text>
                      </Form.Group>
                      {/* Dynamic config fields based on correct MCP's schema */}
                      {(() => {
                        let schema = null;
                        if (selectedMcp.config_schema) {
                          schema = typeof selectedMcp.config_schema === 'string'
                            ? JSON.parse(selectedMcp.config_schema)
                            : selectedMcp.config_schema;
                        }
                        if (!schema) return null;
                        return Object.entries(schema).map(([field, type]) => {
                          if (field === 'store_id') return null; // Skip store_id as we handle it separately
                          return (
                            <Form.Group className="mb-3" key={field}>
                              <Form.Label>
                                {field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ')}
                              </Form.Label>
                              <Form.Control
                                type={type === 'password' ? 'password' : 'text'}
                                value={mcpConfig[field] || ''}
                                onChange={e => handleConfigChange(field, e.target.value)}
                              />
                            </Form.Group>
                          );
                        });
                      })()}
                    </>
                  )}
                </div>
              )}
              
              <div className="mt-4 d-flex gap-2 align-items-center">
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={isSubmitting || !selectedFrameworkId}
                >
                  {isSubmitting ? 'Saving...' : 'Save Framework Settings'}
                </Button>
                {isWooCommerce() && (
                  <Button 
                    type="button"
                    variant="outline-success"
                    disabled={isConfiguring || !mcpConfig.store_id || !mcpConfig.url || !mcpConfig.consumer_key || !mcpConfig.consumer_secret}
                    onClick={handleConfigureWooCommerce}
                  >
                    {isConfiguring ? (<><Spinner size="sm" animation="border" /> Configuring...</>) : 'Configure WooCommerce MCP'}
                  </Button>
                )}
                {isShopify() && (
                  <Button
                    type="button"
                    variant="outline-success"
                    disabled={isConfiguring || !mcpConfig.store_id || !mcpConfig.url}
                    onClick={handleConfigureShopify}
                  >
                    {isConfiguring ? (<><Spinner size="sm" animation="border" /> Configuring...</>) : 'Configure Shopify MCP'}
                  </Button>
                )}
              </div>
            </Form>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default Framework;
